import { AssigneeWarning, UserModel } from 'api/model';
import useUsers from 'contexts/basicData/useUsers';
import { FC, useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import {
  getUserPropsIconArray,
  userPropsArray,
} from 'pages/Employees/filterableUserFields';
import SearchableSelect, {
  SearchableSelectItem,
  SelectOption,
} from './inputs/SearchableSelect';
import { DropdownPosition } from './Dropdown';
import { useAssignmentValidateUsersForDayTransfer } from 'api/assignment/assignment';
import TooltipInfoWarning from './TooltipInfoWarning';

export const PrioritizedDayTransferUserPickerClassName =
  'PrioritizedDayTransferUserPicker';

const StyledSearchableSelect = styled(SearchableSelect)`
  .${PrioritizedDayTransferUserPickerClassName} {
    max-height: 300px !important;
  }
` as typeof SearchableSelect;

const ItemsWrapperStyle = css`
  display: grid;
  grid-template-columns: max-content 1fr max-content max-content;
  grid-auto-flow: row;
  .grid-row:hover ~ .grid-row {
    background-color: yellow;
  }
`;

const EmojiWrapper = styled.div<{ amount: number }>`
  ${({ amount }) => css`
    display: grid;
    grid-template-columns: repeat(${amount}, 1fr);
    grid-auto-flow: row;
  `}
`;

const StyledSelectItemRow = styled.div<{
  selected?: boolean;
  selectable?: boolean;
}>`
  display: contents;
  ${({ selected, theme }) =>
    selected &&
    css`
      & > button {
        background: ${theme.colors.background.selection};
        color: ${theme.colors.foreground.selection};
      }
    `}
  &:hover > button {
    background-color: ${({ theme }) => theme.colors.background.selection};
  }

  ${({ selectable }) =>
    selectable &&
    css`
      cursor: pointer;
      & > button {
        cursor: pointer;
      }
    `}
`;

const StyledSelectItem = styled(SearchableSelectItem)<{
  isNoneItem?: boolean;
  isAvailable: boolean;
  isFiller?: boolean;
}>`
  width: 100%;

  ${({ isNoneItem }) =>
    isNoneItem &&
    css`
      font-style: italic;
    `}
  ${({ isFiller }) =>
    isFiller &&
    css`
      font-style: normal !important;
      font-weight: bold;
    `}

  ${({ isAvailable }) =>
    !isAvailable
      ? `
    font-style: italic;
    color: grey!important;
  `
      : `
    font-style: normal;
    color: black!important;`}


  ${({ isAvailable, theme }) => css`
    color: ${isAvailable
      ? theme.colors.foreground.primary
      : theme.colors.foreground.secondary};
  `};
`;

export interface ExtendedDayTransferUserModel { // now exported for parent access
  canTransfer: boolean;
  user?: UserModel;
  warnings?: AssigneeWarning[];
  postalCode?: string;
  city?: string;
  zipCodeLocalAreaName?: string;
  assignmentIssuesCount?: number;
  isFiller?: boolean;
}

interface PrioritizedDayTransferUserPickerProps {
  autoFocus?: boolean;
  disabled?: boolean;
  dropdownPosition?: DropdownPosition;
  selectedUserId: number | null;
  fromUserId: number;
  date: string;
  onUserPicked(userModel: UserModel | null): void;
  onStateChange?(isOpen: boolean): void;
  onSelectionExtraChanged?(extra: ExtendedDayTransferUserModel | null): void; // new callback
}

const PrioritizedDayTransferUserPicker: FC<
  PrioritizedDayTransferUserPickerProps
> = ({
  autoFocus,
  dropdownPosition,
  disabled,
  selectedUserId,
  fromUserId,
  date,
  onUserPicked,
  onStateChange,
  onSelectionExtraChanged,
}) => {
  const [pickerIsOpen, setPickerIsOpen] = useState(false);

  useEffect(() => {
    if (onStateChange) {
      onStateChange(pickerIsOpen);
    }
  }, [pickerIsOpen, onStateChange]);

  const [options, setOptions] = useState<
    SelectOption<number | null, ExtendedDayTransferUserModel | null>[]
  >([]);

  const users = useUsers();

  const {
    data: validationResult,
    mutate: validateUsers,
    isLoading: isRefetching,
  } = useAssignmentValidateUsersForDayTransfer();

  // Fetch validation when the picker opens
  useEffect(() => {
    if (pickerIsOpen && date) {
      validateUsers({
        data: {
          fromUserId,
          date: new Date(date),
        },
      });
    }
  }, [pickerIsOpen, date, fromUserId, validateUsers]);

  // Update options when validation result changes
  const updateOptions = useCallback(() => {
    const opts: SelectOption<number | null, ExtendedDayTransferUserModel | null>[] = [];

    if (!validationResult) {
      opts.unshift({
        label: 'Välj datum först',
        value: null,
        extra: null,
      });
      setOptions(opts);
      return;
    }

    // Add valid users section
    if (validationResult.validUsers && validationResult.validUsers.length > 0) {
      opts.push({
        label: `✓ Rekommenderade fälttestare (${validationResult.validUsers.length} st)`,
        value: -1,
        extra: {
          canTransfer: false,
          isFiller: true,
        } as any,
      });

      validationResult.validUsers.forEach((userValidation) => {
        const usr = users[userValidation.userId];
        if (!usr) return;

        opts.push({
          label: usr.name,
          description: (
            <EmojiWrapper amount={Object.keys(userPropsArray).length}>
              {getUserPropsIconArray(usr).map((e) => (
                <div aria-label={e.title} key={e.title} title={e.title}>
                  {e.icon}
                </div>
              ))}
            </EmojiWrapper>
          ),
          value: usr.userID,
          extra: {
            canTransfer: true,
            user: usr,
            warnings: userValidation.warnings || [],
            postalCode: userValidation.postalCode,
            city: userValidation.city,
            zipCodeLocalAreaName: userValidation.zipCodeLocalAreaName,
            assignmentIssuesCount: userValidation.assignmentIssues?.length || 0,
          } as ExtendedDayTransferUserModel,
        });
      });
    }

    // Add users with warnings section
    if (
      validationResult.usersWithWarnings &&
      validationResult.usersWithWarnings.length > 0
    ) {
      opts.push({
        label: `⚠ Fälttestare med varningar (${validationResult.usersWithWarnings.length} st)`,
        value: -2,
        extra: {
          canTransfer: false,
          isFiller: true,
        } as any,
      });

      validationResult.usersWithWarnings.forEach((userValidation) => {
        const usr = users[userValidation.userId];
        if (!usr) return;

        opts.push({
          label: usr.name,
          description: (
            <EmojiWrapper amount={Object.keys(userPropsArray).length}>
              {getUserPropsIconArray(usr).map((e) => (
                <div aria-label={e.title} key={e.title} title={e.title}>
                  {e.icon}
                </div>
              ))}
            </EmojiWrapper>
          ),
          value: usr.userID,
          extra: {
            canTransfer: true,
            user: usr,
            warnings: userValidation.warnings || [],
            postalCode: userValidation.postalCode,
            city: userValidation.city,
            zipCodeLocalAreaName: userValidation.zipCodeLocalAreaName,
            assignmentIssuesCount: userValidation.assignmentIssues?.length || 0,
          } as ExtendedDayTransferUserModel,
        });
      });
    }

    // Add blocked users section
    if (
      validationResult.blockedUsers &&
      validationResult.blockedUsers.length > 0
    ) {
      opts.push({
        label: `✗ Ovalbara fälttestare (${validationResult.blockedUsers.length} st)`,
        value: -3,
        extra: {
          canTransfer: false,
          isFiller: true,
        } as any,
      });

      validationResult.blockedUsers.forEach((userValidation) => {
        const usr = users[userValidation.userId];
        if (!usr) return;

        opts.push({
          label: usr.name,
          description: (
            <EmojiWrapper amount={Object.keys(userPropsArray).length}>
              {getUserPropsIconArray(usr).map((e) => (
                <div aria-label={e.title} key={e.title} title={e.title}>
                  {e.icon}
                </div>
              ))}
            </EmojiWrapper>
          ),
          value: usr.userID,
          extra: {
            canTransfer: false,
            user: usr,
            warnings: userValidation.warnings || [],
            postalCode: userValidation.postalCode,
            city: userValidation.city,
            zipCodeLocalAreaName: userValidation.zipCodeLocalAreaName,
            assignmentIssuesCount: userValidation.assignmentIssues?.length || 0,
          } as ExtendedDayTransferUserModel,
        });
      });
    }

    opts.unshift({
      label: 'Ingen vald',
      value: null,
      extra: null,
    });

    setOptions(opts);
  }, [validationResult, users]);

  useEffect(() => {
    if (!isRefetching && validationResult) {
      updateOptions();
    }
  }, [isRefetching, validationResult, updateOptions]);

  const optionRender = useCallback(
    (
      key: string,
      option: SelectOption<number | null, ExtendedDayTransferUserModel | null>,
      onClick: () => void,
      isSelected: boolean
    ) => {
      return (
        <StyledSelectItemRow
          key={`${key}_row`}
          selectable={
            !!(
              option.value === null ||
              (option.extra?.user && option.extra?.canTransfer)
            )
          }
          selected={isSelected}
        >
          <StyledSelectItem
            isAvailable={option.extra?.canTransfer ?? false}
            isFiller={option.extra?.isFiller}
            isNoneItem={option.value === null}
            key={`${key}_label`}
            onClick={onClick}
          >
            <span>
              {option.extra?.warnings && option.extra.warnings.length > 0 && (
                <TooltipInfoWarning
                  warnings={option.extra.warnings}
                  showOnlyTooltip
                  left={20}
                  top={-10}
                />
              )}
              {option.label}
              {(option.extra?.assignmentIssuesCount ?? 0) > 0 && (
                <span
                  style={{
                    marginLeft: '10px',
                    color: '#ff9800',
                    fontSize: '0.9em',
                  }}
                  title={`${option.extra?.assignmentIssuesCount} uppdrag med problem`}
                >
                  ({option.extra?.assignmentIssuesCount} problem)
                </span>
              )}
            </span>
          </StyledSelectItem>
          <StyledSelectItem
            isAvailable={option.extra?.canTransfer ?? false}
            key={`${key}_description`}
            onClick={onClick}
          >
            {option.description}
          </StyledSelectItem>
          <StyledSelectItem
            isAvailable={option.extra?.canTransfer ?? false}
            key={`${key}_city`}
            onClick={onClick}
          >
            {`${option.extra?.city ?? ''} ${
              option.extra?.zipCodeLocalAreaName
                ? `(${option.extra.zipCodeLocalAreaName})`
                : ''
            }`}
          </StyledSelectItem>
          <StyledSelectItem
            isAvailable={option.extra?.canTransfer ?? false}
            key={`${key}_spacer`}
          >
            <div />
          </StyledSelectItem>
        </StyledSelectItemRow>
      );
    },
    []
  );

  const handleItemClick = (
    _userId: number | null,
    _: boolean,
    option: SelectOption<number | null, ExtendedDayTransferUserModel | null>
  ) => {
    if (option.extra?.user && option.extra?.canTransfer) {
      onUserPicked(option.extra.user);
      onSelectionExtraChanged?.(option.extra);
    } else if (option.value === null) {
      onUserPicked(null);
      onSelectionExtraChanged?.(null);
    } else {
      // blocked or filler header selected; do not change selection but clear extra
      onSelectionExtraChanged?.(null);
    }
    // Don't allow selecting blocked users
  };

  return (
    <StyledSearchableSelect
      autoFocus={autoFocus}
      contentClassName={PrioritizedDayTransferUserPickerClassName}
      disabled={disabled}
      dropdownPosition={dropdownPosition}
      itemsWrapperStyle={ItemsWrapperStyle}
      loading={isRefetching}
      maxItems={100}
      onOpenChanged={(isOpen) => {
        setPickerIsOpen(isOpen);
      }}
      onOptionClicked={handleItemClick}
      optionRender={optionRender}
      options={options}
      searchFilter={(searchString, opts) => {
        const lowerNameFilter = searchString.toLowerCase();
        return opts.filter((opt) => {
          if (opt.value === null) {
            return true;
          }
          if (opt.value < 0) {
            return true;
          }
          return (
            (opt.extra?.user?.isActive &&
              (opt.extra?.user?.name?.toLowerCase().includes(lowerNameFilter) ||
                opt.extra?.city?.toLowerCase().includes(lowerNameFilter) ||
                opt.extra?.zipCodeLocalAreaName
                  ?.toLowerCase()
                  .includes(lowerNameFilter) ||
                getUserPropsIconArray(opt.extra.user).some(
                  (e) =>
                    e.enabled &&
                    e.title?.toLowerCase().includes(lowerNameFilter)
                ))) ||
            false
          );
        });
      }}
      selectedValue={selectedUserId}
      showNonOptionSelectedName={
        selectedUserId ? users[selectedUserId]?.name : undefined
      }
    />
  );
};

export default PrioritizedDayTransferUserPicker;
